import { customLocales, type DlCustomLocale } from '@dermloop/static-values';
import { IsGreaterThan } from '@dermloop/utils';
import { Type } from 'class-transformer';
import {
  ArrayNotEmpty,
  IsArray,
  IsBoolean,
  IsDate,
  IsDefined,
  IsEmpty,
  IsEnum,
  IsIn,
  IsObject,
  IsOptional,
  IsString,
  IsUrl,
  IsUUID,
  Matches,
  MaxLength,
  ValidateIf,
  ValidateNested,
  registerDecorator,
  type ValidationArguments,
} from 'class-validator';
import { Audience, AudienceDto } from '../audience/audience.dtos';
import { BaseCreateDto, BaseDto, BaseUpdateDto } from '../base.dto';

/** Declared in priority order: the first type that applies is the one a user sees. */
export enum AnnouncementType {
  Critical = 'critical',
  Warning = 'warning',
  Info = 'info',
  Success = 'success',
}

export const ANNOUNCEMENT_PRIORITY: readonly AnnouncementType[] =
  Object.values(AnnouncementType);

export const ANNOUNCEMENT_TITLE_MAX_LENGTH = 200;
export const ANNOUNCEMENT_BODY_MAX_LENGTH = 2000;
export const ANNOUNCEMENT_LINK_LABEL_MAX_LENGTH = 35;
export const ANNOUNCEMENT_LINK_URL_MAX_LENGTH = 2048;
export const ANNOUNCEMENT_LINK_URL_OPTIONS = {
  protocols: ['http', 'https'],
  require_protocol: true,
};

export type AnnouncementStatus = 'draft' | 'scheduled' | 'active' | 'expired';

export const announcementStatus = (
  announcement: Pick<AnnouncementDto, 'draft' | 'startsAt' | 'endsAt'>,
  now = new Date(),
): AnnouncementStatus => {
  if (announcement.draft) return 'draft';
  if (new Date(announcement.startsAt) > now) return 'scheduled';
  if (announcement.endsAt && new Date(announcement.endsAt) <= now)
    return 'expired';
  return 'active';
};

export const AT_MOST_FIVE = '≤5';

/** A count about users; five or fewer is never reported exactly. */
export type AnnouncementFigure = number | typeof AT_MOST_FIVE;

export const reportedFigure = (count: number): AnnouncementFigure =>
  count > 5 ? count : AT_MOST_FIVE;

/** Impressions count page loads, not users, so they are only as safe to report as the users behind them. */
export const reportedImpressions = (
  impressions: number,
  users: number,
): AnnouncementFigure =>
  reportedFigure(users) === AT_MOST_FIVE ? AT_MOST_FIVE : impressions;

const HAS_VISIBLE_TEXT = /\S/;

const NO_MARKUP = /<\/?[a-z!?][^>]*>/i;

export const hasMarkup = (text: string) => NO_MARKUP.test(text);

/** Rejects anything that looks like an HTML tag; announcement text is shown as plain text. */
function IsPlainText() {
  return (target: object, propertyName: string) =>
    registerDecorator({
      name: 'isPlainText',
      target: target.constructor,
      propertyName,
      validator: {
        validate: (value: unknown) =>
          typeof value !== 'string' || !hasMarkup(value),
        defaultMessage: ({ property }: ValidationArguments) =>
          `${property} must be plain text, without markup`,
      },
    });
}

/** Both or neither: a link needs somewhere to go and something to say. */
function IsLinkPair() {
  return (target: object, propertyName: string) =>
    registerDecorator({
      name: 'isLinkPair',
      target: target.constructor,
      propertyName,
      validator: {
        validate: (_: unknown, { object }: ValidationArguments) =>
          hasLinkPair(object as AnnouncementContentFields),
        defaultMessage: () => 'linkUrl and linkLabel must be set together',
      },
    });
}

export const hasLinkPair = ({
  linkUrl,
  linkLabel,
}: Pick<AnnouncementContentFields, 'linkUrl' | 'linkLabel'>) =>
  !!linkUrl === !!linkLabel;

const hasLink = ({ linkUrl, linkLabel }: AnnouncementContentFields) =>
  linkUrl != null || linkLabel != null;

export interface AnnouncementContentFields {
  title: string;
  body: string;
  linkUrl?: string | null;
  linkLabel?: string | null;
}

export class AnnouncementContentDto implements AnnouncementContentFields {
  @IsIn(customLocales)
  locale: DlCustomLocale;

  @IsString()
  @Matches(HAS_VISIBLE_TEXT)
  @MaxLength(ANNOUNCEMENT_TITLE_MAX_LENGTH)
  @IsPlainText()
  title: string;

  @IsString()
  @Matches(HAS_VISIBLE_TEXT)
  @MaxLength(ANNOUNCEMENT_BODY_MAX_LENGTH)
  @IsPlainText()
  body: string;

  @ValidateIf(hasLink)
  @IsUrl(ANNOUNCEMENT_LINK_URL_OPTIONS)
  @MaxLength(ANNOUNCEMENT_LINK_URL_MAX_LENGTH)
  @IsLinkPair()
  linkUrl?: string;

  @IsOptional()
  @IsString()
  @Matches(HAS_VISIBLE_TEXT)
  @MaxLength(ANNOUNCEMENT_LINK_LABEL_MAX_LENGTH)
  @IsPlainText()
  linkLabel?: string;
}

export class AnnouncementFederationDto {
  @IsUUID()
  federationId: string;

  /** Scopes must lie inside the federation; with none, it reaches the whole federation. */
  @IsDefined()
  @IsObject()
  @ValidateNested()
  @Type(() => AudienceDto)
  audience: AudienceDto;
}

/** One authoring pass; saving creates one independent announcement per federation. */
export class CreateAnnouncementDto extends BaseCreateDto {
  @IsArray()
  @ArrayNotEmpty()
  @IsObject({ each: true })
  @ValidateNested({ each: true })
  @Type(() => AnnouncementFederationDto)
  federations: AnnouncementFederationDto[];

  /** One entry per language the selected federations use. */
  @IsArray()
  @ArrayNotEmpty()
  @IsObject({ each: true })
  @ValidateNested({ each: true })
  @Type(() => AnnouncementContentDto)
  contents: AnnouncementContentDto[];

  @IsEnum(AnnouncementType)
  type: AnnouncementType;

  @Type(() => Date)
  @IsDate()
  startsAt: Date;

  @IsOptional()
  @Type(() => Date)
  @IsDate()
  @IsGreaterThan<CreateAnnouncementDto>('startsAt')
  endsAt?: Date;

  @IsBoolean()
  draft: boolean;
}

// Null is rejected rather than skipped where the column is NOT NULL.
const isSet = (_: unknown, value: unknown) => value !== undefined;

export class UpdateAnnouncementDto extends BaseUpdateDto {
  @IsEmpty({ message: 'federationId cannot be changed after creation' })
  federationId?: never;

  @IsEmpty({ message: 'locale cannot be changed after creation' })
  locale?: never;

  @ValidateIf(isSet)
  @IsEnum(AnnouncementType)
  type?: AnnouncementType;

  @ValidateIf(isSet)
  @IsString()
  @Matches(HAS_VISIBLE_TEXT)
  @MaxLength(ANNOUNCEMENT_TITLE_MAX_LENGTH)
  @IsPlainText()
  title?: string;

  @ValidateIf(isSet)
  @IsString()
  @Matches(HAS_VISIBLE_TEXT)
  @MaxLength(ANNOUNCEMENT_BODY_MAX_LENGTH)
  @IsPlainText()
  body?: string;

  @IsOptional()
  @IsUrl(ANNOUNCEMENT_LINK_URL_OPTIONS)
  @MaxLength(ANNOUNCEMENT_LINK_URL_MAX_LENGTH)
  linkUrl?: string | null;

  @IsOptional()
  @IsString()
  @Matches(HAS_VISIBLE_TEXT)
  @MaxLength(ANNOUNCEMENT_LINK_LABEL_MAX_LENGTH)
  @IsPlainText()
  linkLabel?: string | null;

  @ValidateIf(isSet)
  @Type(() => Date)
  @IsDate()
  startsAt?: Date;

  @IsOptional()
  @Type(() => Date)
  @IsDate()
  endsAt?: Date | null;

  @ValidateIf(isSet)
  @IsBoolean()
  draft?: boolean;

  @ValidateIf(isSet)
  @IsObject()
  @ValidateNested()
  @Type(() => AudienceDto)
  audience?: AudienceDto;
}

export interface ShadowingAnnouncementDto {
  id: string;
  title: string;
  /** The period both announcements are active; no end while both are open-ended. */
  from: string | Date;
  to: string | Date | null;
}

export interface AnnouncementDto extends BaseDto, AnnouncementContentFields {
  federationId: string;
  locale: DlCustomLocale;
  type: AnnouncementType;
  startsAt: string | Date;
  endsAt?: string | Date | null;
  draft: boolean;
  audience: Audience;
}

/**
 * Counted from analytics events once the announcement has ended: only users
 * with product analytics on, and only events sent while the user had a unit in
 * the announcement's federation selected (analytics needs a unit).
 */
export interface AnnouncementStatisticsDto {
  /** Times it was shown: once per page load on web, but once for a reload before that view was sent; "≤5" while five or fewer users saw it. */
  impressions: AnnouncementFigure;
  uniqueUsers: AnnouncementFigure;
  /** Users who dismissed it. */
  dismissals: AnnouncementFigure;
  /** Users who opened its link. */
  linkOpens: AnnouncementFigure;
}

export interface AnnouncementListItemDto extends AnnouncementDto {
  status: AnnouncementStatus;
  /** Users the audience reaches; only while scheduled or active. */
  estimatedReach: AnnouncementFigure | null;
  /** The higher-priority announcement hiding this one from shared users, if any. */
  shadowedBy: ShadowingAnnouncementDto | null;
  /** Only once an ended announcement's figures are settled. */
  statistics: AnnouncementStatisticsDto | null;
}

/** The one announcement a user should see, without whom else it targets; `updated` moves with every admin edit. */
export type ActiveAnnouncementDto = Pick<
  AnnouncementDto,
  'id' | 'type' | 'title' | 'body' | 'linkUrl' | 'linkLabel' | 'updated'
>;
